import { Component } from '@theme/component';

/**
 * Every connected rotator on the page. They all share one ticker rather than each holding its own
 * interval: a collection grid can easily render 24+ cards, and the rotation interval comes from a
 * single theme setting, so per-instance timers would be 24 timers doing identical work at slightly
 * drifting offsets. One ticker also means every card flips at the same moment, which reads as
 * deliberate rather than noisy.
 * @type {Set<BadgeRotator>}
 */
const rotators = new Set();

/** @type {ReturnType<typeof setInterval> | undefined} */
let tickerId;
/** Interval the running ticker was started with, so it is only rebuilt when the value changes. */
let tickerDelay = 0;

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function tick() {
  for (const rotator of rotators) rotator.advance();
}

/**
 * Starts, stops, or restarts the shared ticker to match the current conditions. Rotation stops
 * entirely while the tab is hidden (nothing to see, no reason to burn timers) and never starts at
 * all under reduced-motion, where the badges simply stay stacked as they render without JS.
 */
function syncTicker() {
  const [first] = rotators;
  const delay = first?.intervalMs ?? 0;
  const shouldRun = delay > 0 && !document.hidden && !prefersReducedMotion();

  if (!shouldRun) {
    clearInterval(tickerId);
    tickerId = undefined;
    tickerDelay = 0;
    return;
  }

  if (tickerId && delay === tickerDelay) return;

  clearInterval(tickerId);
  tickerDelay = delay;
  tickerId = setInterval(tick, delay);
}

document.addEventListener('visibilitychange', syncTicker);

/**
 * Cycles a product card's metafield badges one at a time instead of stacking them, so a product
 * carrying several (e.g. "ONLINE EXCLUSIVE" and "GET 30K OFF MIN 499K") shows one per beat in the
 * same spot.
 *
 * The markup already arrives in the resting state this component would otherwise have to create:
 * every badge past the first ships with `hidden` set server-side (see the Liquid). So without JS,
 * with slow JS, or under reduced-motion, the card shows exactly one badge and simply never
 * advances — never a stack that collapses a moment later, which is what a JS-only approach caused.
 *
 * @extends {Component}
 */
class BadgeRotator extends Component {
  #index = 0;

  connectedCallback() {
    super.connectedCallback();

    // One badge (or none) has nothing to rotate through — leave it alone entirely so it never
    // takes part in the ticker.
    if (this.badges.length < 2 || prefersReducedMotion()) return;

    this.#show(0);
    rotators.add(this);
    syncTicker();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    rotators.delete(this);
    syncTicker();
  }

  /** @returns {HTMLElement[]} */
  get badges() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('.product-badges__badge')]);
  }

  /** @returns {number} Rotation interval in milliseconds. */
  get intervalMs() {
    return (Number(this.dataset.interval) || 3) * 1000;
  }

  /** Advances to the next badge, wrapping back to the first. */
  advance() {
    const count = this.badges.length;
    if (count < 2) return;

    this.#index = (this.#index + 1) % count;
    this.#show(this.#index);
  }

  /**
   * @param {number} activeIndex - Index of the badge that should be visible.
   */
  #show(activeIndex) {
    this.badges.forEach((badge, index) => {
      const hidden = index !== activeIndex;
      // Only write when the value actually changes — an unconditional assignment here would be a
      // DOM mutation per badge per tick on every card, which is what makes Component's own
      // MutationObserver churn (same lesson as the announcement bar countdown).
      if (badge.hidden !== hidden) badge.hidden = hidden;
    });
  }
}

if (!customElements.get('badge-rotator')) customElements.define('badge-rotator', BadgeRotator);
